import { DateTime, Duration } from 'luxon';
import { rem } from 'polished';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { translate } from '@vimeo/i18n-runtime';
import { Header } from '@vimeo/iris/typography';
import { getScheduleTime } from '@vimeo/live-player-cards';

import { Schedule, ScheduleFrequencyType } from '../../types';
import { SECONDS_IN_MINUTE, SECONDS_IN_HOUR } from '../../utils/constants';
import {
  getEventScheduleDayOfMonth,
  getEventScheduleFrequency,
  getEventScheduleWeekDays,
} from '../../utils/helpers';

const getTimeLeftString = (secondsUntilLive?: number) => {
  if (!secondsUntilLive) {
    return '';
  }

  const duration = Duration.fromObject({ seconds: secondsUntilLive });

  // If there's more than an hour left, show HRS MINS or else show MINS SECS.
  if (secondsUntilLive > 60 * SECONDS_IN_MINUTE) {
    return duration.toFormat(" hh 'hrs' mm 'mins'");
  }
  return duration.toFormat(" m 'mins' s 'secs'");
};

type Props = {
  schedule: Schedule;
};

export default function LiveSchedule({ schedule }: Props) {
  let countdownInterval: number;
  const [secondsUntilLive, setSecondsUntilLive] = useState<number>(0);
  const scheduleFrequencyType = getEventScheduleFrequency(schedule);

  useEffect(() => {
    const scheduleDate = schedule.startTime;

    const nowDateTime = DateTime.fromJSDate(new Date());
    let startDateTime = DateTime.fromISO(scheduleDate);

    // For one-time schedule, startTime should match today's date if it starts today
    const startingTodaySingle =
      nowDateTime.toFormat('LL/dd/yyyy') === startDateTime.toFormat('LL/dd/yyyy');

    // For weekly schedule, the day of the week should match if it starts today
    const startingTodayWeekly =
      scheduleFrequencyType === ScheduleFrequencyType.WEEKLY &&
      getEventScheduleWeekDays(schedule).indexOf(new Date().getDay()) >= 0;

    // For monthly schedule, the day of the month should match if it starts today
    const startingTodayMonthly =
      scheduleFrequencyType === ScheduleFrequencyType.MONTHLY &&
      getEventScheduleDayOfMonth(schedule) === nowDateTime.day;

    // We need to explicitly set the day as schedule.startTime day might be in the past and this will lead to incorrect countdown on showcase view page
    if (startingTodayWeekly || startingTodaySingle || startingTodayMonthly) {
      startDateTime = startDateTime.set({
        day: nowDateTime.day,
        month: nowDateTime.month,
        year: nowDateTime.year,
      });
    }

    /**
     * Use this diff to only show the countdown if under 12 hours until start time.
     * Also, do not show countdown if past time (diff > 0 seconds)
     */
    const diff = startDateTime.diff(nowDateTime, ['hours', 'seconds']);
    const isWithinStartTimeDiff = diff.hours < 12 && diff.seconds > 0;

    const isLiveToday =
      isWithinStartTimeDiff && (startingTodaySingle || startingTodayWeekly || startingTodayMonthly);

    setSecondsUntilLive(isLiveToday ? SECONDS_IN_HOUR * diff.hours + diff.seconds : 0);
  }, [schedule]);

  useEffect(() => {
    const updateCountdown = () => {
      // If the countdowns over run the onCountdownEnd handler and clear the interval
      if (secondsUntilLive <= 0) {
        clearInterval(countdownInterval);
      } else {
        setSecondsUntilLive(secondsUntilLive - 1);
      }
    };

    countdownInterval = setInterval(updateCountdown, 1000);
    return () => {
      if (countdownInterval) {
        clearInterval(countdownInterval);
      }
    };
  }, [secondsUntilLive]);

  if (secondsUntilLive) {
    return (
      <Countdown>
        <Message>
          {secondsUntilLive > 15 * SECONDS_IN_MINUTE
            ? translate({
                singular: 'Starting in {TIME}',
                replacements: {
                  TIME: getTimeLeftString(secondsUntilLive),
                },
              })
            : translate('Starting soon...')}
        </Message>
      </Countdown>
    );
  }

  // Messaging for one-time schedule
  if (
    scheduleFrequencyType === ScheduleFrequencyType.SINGLE ||
    scheduleFrequencyType === ScheduleFrequencyType.YEARLY
  ) {
    return (
      <Countdown>
        <Message>This event will start {getScheduleTime(schedule)}</Message>
      </Countdown>
    );
  }

  // Messaging for weekly schedule. i.e - "Every Monday and Wednesday at 8pm"
  return (
    <Countdown>
      <Message>{getScheduleTime(schedule)}</Message>
    </Countdown>
  );
}

const Countdown = styled.div`
  padding: ${rem(15)} 0;
  text-align: center;
`;

const Message = styled(Header).attrs({ size: '3' })`
  color: white;
`;
